// React
import React, { useCallback, useEffect, useRef, useState } from "react";

// MUI
import { AutoAwesome } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

// Hooks
import useHotkeys, { hotkeyToText, isMac } from "hooks/ui/useHotkeys";

// Utils
import { isClientUser } from "utils/auth.jsx";

import AiPanel from "./AiPanel";
import {
  AI_CHAT_PATH,
  ENTITY_CHAT_OPEN_EVENT,
  loadAiStatus,
  setAiOpen,
  TOGGLE_EVENT,
  useAiStore,
} from "./aiStore";
import usePageContext from "./usePageContext";

const HOTKEY = "mod+k";
// Set on <html> while the button is on screen: components/Chat moves its own button above it
const HTML_FLAG = "aiAssistant";

/**
 * AI assistant: floating button (bottom right) and chat panel, for operators only and only when
 * the backend has it on (GET ai/status/). Opens with ⌘/Ctrl+K and with the window event
 * "ai-assistant:toggle" (navbar button). Mounted once in MainLayout.
 * On the Chat page (AI_CHAT_PATH) it steps aside: the page is the chat and owns ⌘/Ctrl+K.
 */
export default function AiAssistant() {
  const { t } = useTranslation("aiAssistant");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const context = usePageContext();
  const { open } = useAiStore();
  const [clientUser] = useState(isClientUser);
  const [enabled, setEnabled] = useState(false);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (clientUser) return undefined;
    let active = true;
    loadAiStatus().then((on) => {
      if (active) setEnabled(on);
    });
    return () => {
      active = false;
    };
  }, [clientUser]);

  const onChatPage = pathname === AI_CHAT_PATH;
  const available = enabled && !clientUser && !onChatPage;

  // The panel never stays open behind the Chat page (nor springs back when leaving it)
  useEffect(() => {
    if (onChatPage) setAiOpen(false);
  }, [onChatPage]);

  const openPanel = useCallback(() => {
    const focused = document.activeElement;
    returnFocusRef.current = focused instanceof HTMLElement ? focused : null;
    setAiOpen(true);
    // The panel may still be mounting
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const closePanel = useCallback(() => {
    setAiOpen(false);
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    requestAnimationFrame(() => {
      if (target?.isConnected) target.focus();
      else fabRef.current?.focus();
    });
  }, []);

  // ⌘K / navbar: opens; with the panel open, the first press moves the focus into it and the
  // next one closes it
  const toggle = useCallback(() => {
    if (!open) {
      openPanel();
      return;
    }
    const panel = inputRef.current?.closest("section");
    if (panel && !panel.contains(document.activeElement)) inputRef.current?.focus();
    else closePanel();
  }, [open, openPanel, closePanel]);

  useHotkeys(
    {
      [HOTKEY]: {
        handler: toggle,
        description: t("shortcut.toggle"),
        group: t("ui:shortcuts.groups.general"),
        // Not behind a modal dialog (the panel would open under it)
        allowInDialog: false,
      },
    },
    { enabled: available }
  );

  // Navbar button and other triggers: a plain open / close (a click moves the focus to the button)
  useEffect(() => {
    if (!available) return undefined;
    const onToggle = () => (open ? closePanel() : openPanel());
    window.addEventListener(TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(TOGGLE_EVENT, onToggle);
  }, [available, open, openPanel, closePanel]);

  // The comments chat (components/Chat) takes the same corner: one at a time
  useEffect(() => {
    if (!available) return undefined;
    const onEntityChatOpen = () => setAiOpen(false);
    window.addEventListener(ENTITY_CHAT_OPEN_EVENT, onEntityChatOpen);
    return () => window.removeEventListener(ENTITY_CHAT_OPEN_EVENT, onEntityChatOpen);
  }, [available]);

  // Tells components/Chat that the button is there
  useEffect(() => {
    if (!available) return undefined;
    const root = document.documentElement;
    root.dataset[HTML_FLAG] = "on";
    return () => {
      delete root.dataset[HTML_FLAG];
    };
  }, [available]);

  // Internal links of the replies: navigate in the app and step out of the way
  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      returnFocusRef.current = null;
      setAiOpen(false);
    },
    [navigate]
  );

  // "Vai in chat": same conversation, full screen, with the context of this page
  const handleOpenChat = useCallback(() => {
    returnFocusRef.current = null;
    setAiOpen(false);
    navigate(AI_CHAT_PATH, { state: { context } });
  }, [navigate, context]);

  if (!available) return null;

  const shortcut = hotkeyToText(HOTKEY);

  return (
    <>
      {!open && (
        <Tooltip
          placement="left"
          title={
            <span className="inline-flex items-center gap-1.5">
              {t("fab.tooltip")}
              <span className="rounded bg-white/15 px-1 text-[11px]">{shortcut}</span>
            </span>
          }
        >
          <button
            ref={fabRef}
            type="button"
            onClick={openPanel}
            aria-label={t("fab.ariaLabel")}
            aria-keyshortcuts={isMac() ? "Meta+K" : "Control+K"}
            className="fixed right-6 bottom-6 z-(--z-fab) inline-flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 bg-white text-primary shadow-card-hover transition-[transform,box-shadow] duration-150 ease-out hover:-translate-y-px hover:shadow-popover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0 print:hidden [html[data-entity-chat-open]_&]:hidden"
          >
            <AutoAwesome sx={{ fontSize: 22 }} />
          </button>
        </Tooltip>
      )}
      {open && (
        <AiPanel
          context={context}
          onClose={closePanel}
          onNavigate={handleNavigate}
          onOpenChat={handleOpenChat}
          inputRef={inputRef}
        />
      )}
    </>
  );
}
